import React, { useEffect }  from "react";
import { useSelector, useDispatch } from 'react-redux';
import { TextField } from "formik-material-ui";
import { Button, Divider, Grid, MenuItem } from "@material-ui/core";
import { Formik, Form, Field } from "formik";
import { useEnqueueSnackbar } from "../../../hooks/useEnqueueSnackbar";
import * as Yup from "yup";
import PropTypes from "prop-types";
import SaveIcon from '@material-ui/icons/Save';
import CancelIcon from '@material-ui/icons/Cancel';
import styles from "./DeviceEditor.styles";
import {
    FindEntity,
    entityUpdate,
    entityCreate,
    entitySettingsUpdate,
} from "../../../state/slices/entities";
import {
    updateEntity,
    createNewEntity,
    updateEntitySettings,
    selectEntityById,
} from "../../../state/slices/CoreEntity";
import Title from "../../Title";
import DeviceService from "../../../services/DeviceService";
import apiClient from "../../../auth/apiClient";
import * as c from "../../../constants";
import { useCoreEntityContext } from "../../../hooks/useCoreEntitySlice";
import { selectOrgTreeEntityById } from "../../../state/slices/OrgTree";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useFeatureFlag } from "../../../hooks/useFeatureFlags";

const deviceService = new DeviceService(apiClient);

const DeviceEditor = ({ entityID, parentEntityID, parentEntityData, onCancel, onSaveComplete, onDeleteComplete, entityType, deviceMode }) => {
    const classes = styles();
    const dispatch = useDispatch();
    const currentUser = useSelector(state => state.user);
    const isEditMode = entityID != null;
    const useCoreEntitySlice = useCoreEntityContext();
    const hotelIntegration = useFeatureFlag("Hotel Integrations");
    const isPointOfSaleEnabled = useFeatureFlag('Features.Point of Sale');
    const { doorDeviceMode } = useFlags() || {};
    const enqueueSnackbar = useEnqueueSnackbar();

    const entityState = useSelector((state) => {
        let entity = useCoreEntitySlice ? selectEntityById(state, entityID) : FindEntity(state?.entities?.EntityList, entityID);
        if (useCoreEntitySlice && !entity?.entityid) entity = selectOrgTreeEntityById(state, entityID);

        const createEntity = { entityid: null, parententityid: parentEntityID, name: "", description: "", deviceMode: "" };

        return entityID ? entity : createEntity;
    });

    const getPayloadContent = (values) => {
        return {
            path: "parkingdevice" + ((isEditMode) ? "" : "/v2/" + parentEntityID),
            entity: {
                DeviceID: entityID,
                ParentEntityID: parentEntityID,
                Name: values.name,
                description: values.description,
                deviceMode: values.deviceMode,
            }
        };
    };

    const modes = {
        "Entry": "Entry",
        "Exit": "Exit",
		"Kiosk": "Pay on Foot",
        "Diagnostic": "Diagnostic",
        "Door": "Door",
        "Hotel Interface": "Hotel Interface",
        "Point of Sale": "Point of Sale",
    };


    const genericSchema = Yup.object().shape({
        deviceMode: Yup.string().required("Device mode is required"),
        name: Yup.string().min(2, "Minimum of 2 characters").max(50, "Maximum of 50 characters").required("Required"),
        description: Yup.string().max(200, "Maximum of 200 characters").nullable(),
    });
    //if mode is "create", the entity id is the parent entity id
    //if mode is "create", look for another prop of type
    //if mode is "edit", entityid is the editing entity id
    //probably not needed but need to test and fix
    useEffect(() => {//if user or entity changes
        return () => {}
    }, [entityState]);

    return (
        <div style={{ padding: 10 }} data-testid="DeviceEditorTitle">
            <Title><span className={classes.camelCase}>{(isEditMode) ? "Edit Device" : "Create Device"}</span></Title>
            <Divider style={{ marginBottom: 15 }}></Divider>
            <Formik
                onSubmit={async (values) => {
                    values = { ...values, name: values.name?.trim() };
                    var _payload = getPayloadContent(values);
                    let result;
                    let secondaryUpdateError = false;

                    if (isEditMode) {
                        let resp;
                        try {
                            resp = await deviceService.getEntity(_payload.entity.ParentEntityID, _payload.entity.DeviceID);
                        } catch {
                            secondaryUpdateError = true;
                        }
                        if (!secondaryUpdateError) {
                            _payload.entity.DestinationEntityID = resp.data?.destinationEntityID ? resp.data?.destinationEntityID : null;
                            result = await dispatch(useCoreEntitySlice ? updateEntity(_payload) : entityUpdate(_payload));
                        }
                    } else {
                        if (parentEntityData.typeID == c.ENTITY_TYPE.Area) {
                            _payload.entity.DestinationEntityID = parentEntityData.parentEntityID;
                        } else {
                            _payload.entity.DestinationEntityID = _payload.entity.ParentEntityID;
                        }

                        result = await dispatch(useCoreEntitySlice ? createNewEntity(_payload) : entityCreate(_payload));
                    }

                    if (secondaryUpdateError || result.error) {
                        enqueueSnackbar("Unable to save updates. Please try again.", { variant: "error", tag: "FailedToSaveUpdates" });
                    } else {
                        onSaveComplete();
                    }
                }}
                enableReinitialize
                validateOnChange={true}
                initialValues={{
                    name: entityState.name,
                    description: (isEditMode) ? entityState.details?.description : entityState.description,
                    deviceMode: deviceMode
                }}
                validationSchema={genericSchema}
            >
                {({ isSubmitting }) => (
                    <Form style={{ width: "100%" }}>
                        <Grid container spacing={2} style={{ width: "100%" }}>
                            <Grid item xs={6}>
                            <Field
                                name="deviceMode"
                                label="Device Mode"
                                fullWidth
                                variant="outlined"
                                select
                                component={TextField}
                                inputProps={{ 'data-testid': 'device-mode-select' }}
                            >
                                {(entityType !== c.ENTITY_TYPE.ValetArea
                                ? Object.entries(modes)
                                    .filter(([label, value]) => {
                                        if (value === c.DEVICE_MODE.UNKNOWN) return false;
                                        if (value === "Hotel Interface" && !hotelIntegration) return false;
                                        if (value === "Door" && !(doorDeviceMode ?? false)) return false;
                                        if (value === "Point of Sale" && !isPointOfSaleEnabled) return false;
                                        return true;
                                    })
                                    .map(([label, value]) => (
                                        <MenuItem key={label} value={label}>
                                        {value}
                                        </MenuItem>
                                    ))
                                : [
                                    <MenuItem key={modes["Point of Sale"]} value={modes["Point of Sale"]}>
                                        Point of Sale
                                    </MenuItem>
                                    ]
                                )}
                            </Field>
                            </Grid>
                            <Grid item xs={12}>
                                <Field data-testid="nameField" fullWidth variant="outlined" label="Name" name="name" component={TextField} />
                            </Grid>
                            <Grid item xs={12}>
                                <Field
                                    data-testid="descriptionField"
                                    fullWidth
                                    multiline
                                    rowsMax={4}
                                    variant="outlined"
                                    label="Description"
                                    name="description"
                                    component={TextField} />
                            </Grid>
                            <Grid item xs={12} style={{ textAlign: "right" }}>
                                <Button
                                    data-testid="submitButton"
                                    style={{ marginRight: 10 }}
                                    type="submit"
                                    startIcon={<SaveIcon />}
                                    variant="contained"
                                    color="primary"
                                    disabled={isSubmitting}
                                >
                                    {isEditMode ? "Update" : "Create"}
                                </Button>
                                <Button
                                    startIcon={<CancelIcon />}
                                    variant="contained"
                                    onClick={onCancel}
                                >
                                    Cancel
                                </Button>
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </div>
    );
}

DeviceEditor.defaultProps = { entityID: null, parentEntityID: null, parentEntityData: undefined };
DeviceEditor.propTypes = { entityID: PropTypes.string, parentEntityID: PropTypes.string, parentEntityData: PropTypes.object };

export default DeviceEditor;